import {
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiTextArea,
  EuiButton,
  EuiSpacer,
  EuiText,
} from "@elastic/eui";
import React, { useEffect, useState } from "react";
import { Business } from "api/entities/Business";
import { useNavigate } from "react-router-dom";
import { setBusinessBuilderAction } from "slices/NewOrderCreateSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "slices/RootReducer";
import { IsEmail, IsZipCode } from "../../utils/Validation";
import { useCreateBusinessMutation } from "api/rtkQueryApi/BusinessesApi";

export const NewOrderBusinessInfo: React.FC = () => {
  const { businessBuilder } = useSelector((state: RootState) => state.newOrder);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [focussed, setFocussed] = React.useState<string[]>([]);
  const [continuing, setContinuing] = useState(false);
  const [error, setError] = useState("");

  const [tempBusiness, setTempBusiness] = useState<Business>({
    name: "",
    clientFirstName: "",
    clientLastName: "",
    address: {
      street: "",
      city: "",
      state: "",
      zip: "",
      country: "usa",
    },
    phone: "",
    email: "",
    website: "",
    description: "",
  });

  const [createBusiness, createBusinessRequest] = useCreateBusinessMutation();

  useEffect(() => {
    if (businessBuilder) {
      setTempBusiness(businessBuilder);
    }
  }, [businessBuilder]);

  useEffect(() => {
    setError("");
  }, [tempBusiness]);

  const addFocussed = (field: string) => {
    if (!focussed.includes(field)) {
      setFocussed([...focussed, field]);
    }
  };

  const hasFocussed = (field: string) => {
    return focussed.includes(field);
  };

  const onBusinessNameChange = (name: string) => {
    setTempBusiness({ ...tempBusiness, name });
  };

  const onBusinessClientFirstNameChange = (clientFirstName: string) => {
    setTempBusiness({ ...tempBusiness, clientFirstName });
  };

  const onBusinessClientLastNameChange = (clientLastName: string) => {
    setTempBusiness({ ...tempBusiness, clientLastName });
  };

  const onBusinessEmailChange = (email: string) => {
    setTempBusiness({ ...tempBusiness, email });
  };

  const onBusinessPhoneChange = (phone: string) => {
    setTempBusiness({ ...tempBusiness, phone });
  };

  const onBusinessWebsiteChange = (website: string) => {
    setTempBusiness({ ...tempBusiness, website });
  };

  const onBusinessDescriptionChange = (description: string) => {
    setTempBusiness({ ...tempBusiness, description });
  };

  const onStreetChange = (street: string) => {
    setTempBusiness({
      ...tempBusiness,
      address: { ...tempBusiness.address, street },
    });
  };

  const onCityChange = (city: string) => {
    setTempBusiness({
      ...tempBusiness,
      address: { ...tempBusiness.address, city },
    });
  };

  const onStateChange = (state: string) => {
    setTempBusiness({
      ...tempBusiness,
      address: { ...tempBusiness.address, state },
    });
  };

  const onZipChange = (zip: string) => {
    setTempBusiness({
      ...tempBusiness,
      address: { ...tempBusiness.address, zip },
    });
  };

  const onCountryChange = (country: string) => {
    setTempBusiness({
      ...tempBusiness,
      address: { ...tempBusiness.address, country },
    });
  };

  const onSaveForLaterClick = async () => {
    setError("");
    setContinuing(false);

    const { data: business } = await createBusiness({
      business: {
        ...tempBusiness,
      },
    });

    if (!business) {
      setError("Something went wrong, please try again");
      return;
    }

    navigate("/");
  };

  const onNextClick = async () => {
    setError("");
    setContinuing(true);

    if (!businessBuilder.isCreated) {
      const { data: business } = await createBusiness({
        business: {
          ...tempBusiness,
        },
      });

      if (!business) {
        setError("Something went wrong, please try again");
        return;
      }

      dispatch(setBusinessBuilderAction({ ...business!, isCreated: true, isOverrideSetupFee: false, overrideSetupFee: 0 }));
    }

    navigate("/new/product");
  };

  const validFields =
    IsEmail(tempBusiness.email) && IsZipCode(tempBusiness.address.zip);

  const fieldsComplete =
    tempBusiness.name &&
    tempBusiness.clientFirstName &&
    tempBusiness.clientLastName &&
    tempBusiness.email &&
    tempBusiness.phone &&
    tempBusiness.address.street &&
    tempBusiness.address.city &&
    tempBusiness.address.state &&
    tempBusiness.address.zip &&
    tempBusiness.address.country;

  const nextReady = validFields && fieldsComplete;

  return (
    <>
      <EuiFlexGroup>
        <EuiFlexItem>
          <EuiFormRow label="Business Name">
            <EuiFieldText
              value={tempBusiness.name}
              onChange={(e) => onBusinessNameChange(e.target.value)}
            />
          </EuiFormRow>
          <EuiFormRow label="Description">
            <EuiTextArea
              value={tempBusiness.description}
              onChange={(e) => onBusinessDescriptionChange(e.target.value)}
            />
          </EuiFormRow>
          <EuiFormRow
            label="Client First Name"
            isInvalid={
              hasFocussed("Client First Name") && !tempBusiness?.clientFirstName
            }
            error={"Client First Name is Required"}
          >
            <EuiFieldText
              value={tempBusiness.clientFirstName}
              onChange={(e) => onBusinessClientFirstNameChange(e.target.value)}
              onFocus={() => addFocussed("Client First Name")}
            />
          </EuiFormRow>
          <EuiFormRow
            label="Client Last Name"
            isInvalid={
              hasFocussed("Client Last Name") && !tempBusiness?.clientLastName
            }
            error={"Client Last Name is Required"}
          >
            <EuiFieldText
              value={tempBusiness.clientLastName}
              onChange={(e) => onBusinessClientLastNameChange(e.target.value)}
              onFocus={() => addFocussed("Client Last Name")}
            />
          </EuiFormRow>
          <EuiFormRow
            label="Client Email"
            isInvalid={
              hasFocussed("Client Email") && !IsEmail(tempBusiness.email)
            }
            error={"Valid Email Required"}
          >
            <EuiFieldText
              value={tempBusiness.email}
              onChange={(e) => onBusinessEmailChange(e.target.value)}
              onFocus={() => addFocussed("Client Email")}
            />
          </EuiFormRow>
          <EuiFormRow label="Client Phone">
            <EuiFieldText
              value={tempBusiness.phone}
              onChange={(e) => onBusinessPhoneChange(e.target.value)}
            />
          </EuiFormRow>
          <EuiFormRow label="Website">
            <EuiFieldText
              value={tempBusiness.website}
              onChange={(e) => onBusinessWebsiteChange(e.target.value)}
            />
          </EuiFormRow>
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiFormRow label="Street">
            <EuiFieldText
              value={tempBusiness.address.street}
              onChange={(e) => onStreetChange(e.target.value)}
            />
          </EuiFormRow>
          <EuiFormRow label="City">
            <EuiFieldText
              value={tempBusiness.address.city}
              onChange={(e) => onCityChange(e.target.value)}
            />
          </EuiFormRow>
          <EuiFormRow label="State">
            <EuiFieldText
              value={tempBusiness.address.state}
              onChange={(e) => onStateChange(e.target.value)}
            />
          </EuiFormRow>
          <EuiFormRow
            label="Zip"
            isInvalid={
              hasFocussed("Zip") && !IsZipCode(tempBusiness.address.zip)
            }
            error={"5 Digit Zip Code Required"}
          >
            <EuiFieldText
              value={tempBusiness.address.zip}
              onChange={(e) => onZipChange(e.target.value)}
              onFocus={() => addFocussed("Zip")}
            />
          </EuiFormRow>
          <EuiFormRow label="Country">
            <EuiFieldText
              value={tempBusiness.address.country}
              onChange={(e) => onCountryChange(e.target.value)}
            />
          </EuiFormRow>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer />

      {error && (
        <EuiFormRow>
          <EuiText color="danger">{error}</EuiText>
        </EuiFormRow>
      )}

      <EuiFormRow>
        <EuiFlexGroup>
          <EuiFlexItem grow={false}>
            <EuiButton
              disabled={
                !nextReady || (createBusinessRequest.isLoading && continuing)
              }
              onClick={onSaveForLaterClick}
              isLoading={createBusinessRequest.isLoading && !continuing}
            >
              Save for Later
            </EuiButton>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton
              fill
              disabled={
                !nextReady || (createBusinessRequest.isLoading && !continuing)
              }
              onClick={onNextClick}
              isLoading={createBusinessRequest.isLoading && continuing}
            >
              Continue
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFormRow>
    </>
  );
};
